import React from "react"
import PropTypes from "prop-types"
import { Route, Redirect } from "react-router-dom"
import { isTokenValid, clearSession } from "helpers/auth_helpers"
import RequireRole from "components/RequireRole"

const Authmiddleware = ({
  component: Component,
  layout: Layout,
  isAuthProtected,
  permissionBy,
  rolesRequeridos,
  objetoRequerido,
  accionRequerida,
  publico,
  error,
  ...rest
}) => (
  <Route
    {...rest}
    render={props => {
      // BDSJEA-130: un token puede existir en localStorage y no ser valido
      // para esta sesion (vencido, corrupto, de otro ambiente). No alcanza
      // con chequear que exista -- hay que validar el "exp".
      if (isAuthProtected && !isTokenValid()) {
        clearSession()
        return (
          <Redirect
            to={{
              pathname: "/iniciar-sesion",
              state: { from: props.location },
            }}
          />
        )
      }

      if (error) {
        return <Component {...props} />
      }

      // Login, logout y demás rutas de publicRoutes. No hay nada que
      // autorizar: no hay sesión todavía, o se está cerrando. Se atiende
      // acá arriba y no con el flag "publico" porque App.js renderiza
      // publicRoutes con su propio map, que no pasa esas props: confiar en
      // el flag dejaba el cierre de sesión bloqueado por la regla de
      // denegar por defecto.
      if (!isAuthProtected) {
        return (
          <Layout>
            <Component {...props} />
          </Layout>
        )
      }

      const contenido = (
        <Component permissionBy={permissionBy ? permissionBy : ""} {...props} />
      )

      // BDSJEA-14: este es el ÚNICO punto donde se decide si una pantalla se
      // muestra o no. Antes la decisión estaba (mal) en el componente Permission,
      // que falla abierto: si el objeto no está entre los permisos del usuario no
      // encuentra con qué comparar y renderiza igual. Escribiendo la URL a mano,
      // eso alcanzaba para entrar a Administración con perfil de solo consulta.
      //
      // Acá es al revés: toda ruta protegida tiene que declarar explícitamente
      // rolesRequeridos, objetoRequerido o publico. La que no declara nada se
      // deniega. Es incómodo al agregar una pantalla nueva, y es a propósito:
      // el olvido se paga con un 403 en desarrollo y no con una fuga en producción.
      let protegido
      if (rolesRequeridos && rolesRequeridos.length) {
        protegido = (
          <RequireRole roles={rolesRequeridos} pantalla>
            {contenido}
          </RequireRole>
        )
      } else if (objetoRequerido) {
        protegido = (
          <RequireRole
            objeto={objetoRequerido}
            accion={accionRequerida || "consulta"}
            pantalla
          >
            {contenido}
          </RequireRole>
        )
      } else if (publico) {
        // Login, logout, 404, cambio de clave propia: no mapean a ningún objeto
        // del sistema. Cualquier usuario autenticado puede entrar.
        protegido = contenido
      } else {
        console.error(
          `[seguridad] La ruta ${props.location?.pathname} no declara ` +
            "objetoRequerido, rolesRequeridos ni publico. Se deniega el acceso. " +
            "Declararla en routes/index.js.",
        )
        protegido = (
          <RequireRole roles={["__RUTA_SIN_DECLARAR__"]} pantalla>
            {contenido}
          </RequireRole>
        )
      }

      return <Layout>{protegido}</Layout>
    }}
  />
)

Authmiddleware.propTypes = {
  isAuthProtected: PropTypes.bool,
  rolesRequeridos: PropTypes.arrayOf(PropTypes.string),
  objetoRequerido: PropTypes.string,
  accionRequerida: PropTypes.oneOf(["consulta", "modificacion"]),
  publico: PropTypes.bool,
  component: PropTypes.any,
  location: PropTypes.object,
  layout: PropTypes.any,
}

export default Authmiddleware
